import { date } from '@ipfabric/utils';
import { type FC, type MouseEventHandler, useEffect, useState } from 'react';
import { Calendar } from './Calendar';

import './dateTimePicker.css';

import { Overlay } from '@ipfabric/ui-components/Overlay/Overlay';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { useManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import { ESCAPE } from '@ipfabric/ui-components/utils/keyboard/keyboardKeys';
import Dropdown from 'components/Dropdown';
import { FormInputError } from 'components/FormInputError/FormInputError';
import { format as format_1, isValid, parse, startOfDay } from 'date-fns';
import './dropdown.css';
import { withTargetValue } from '@ipfabric/ui-components/utils/events/withTargetValue';

interface Props {
  inputDateFormats?: string[];
  outputDateFormat: string;
  onClose: () => void;
  onChange: (val: { value: number; operator: string }) => void;
  timezone: date.Timezone;
  value: string;
  comparator?: string;
  withoutComaparator?: boolean;
}

const dateTimeComparators = ['>=', '>', '<=', '<', '='].map((operator) => ({
  value: operator,
  label: operator,
}));

export const DateTimePicker: FC<Props> = (props) => {
  const [comparator, setComparator] = useState(props.comparator ?? '>=');
  const [inputValue, setInputValue] = useState('');

  const dateFromInputValue = parse(
    inputValue,
    props.outputDateFormat,
    new Date(0),
  );
  const isDateValid =
    isValid(dateFromInputValue) && dateFromInputValue.getTime() > 0;

  useEffect(
    () =>
      tryDateParse(props.inputDateFormats, props.outputDateFormat, props.value),
    [props.inputDateFormats, props.outputDateFormat, props.value],
  );

  const handleKeydown = useStableCallback((e: KeyboardEvent) => {
    if (e.key === ESCAPE) {
      props.onClose();
    }
  });

  useManagedDomEventListeners([window, 'keydown', handleKeydown]);

  const tryDateParse = (
    inputDateFormats: string[] | undefined,
    outputDateFormat: string,
    value: string,
  ) => {
    let successParsing = false;
    if (inputDateFormats) {
      for (const format of inputDateFormats) {
        const parsedDate = parse(value, format, new Date(0));
        if (isValid(parsedDate)) {
          setInputValue(format_1(parsedDate, outputDateFormat));
          successParsing = true;
        }
      }
    }
    if (!successParsing) {
      const today = startOfDay(new Date()).valueOf();
      setInputValue(format_1(today, outputDateFormat));
    }
  };

  const handleSaveButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    if (isDateValid) {
      const dt = date.userTimeZoneToUtc(dateFromInputValue, props.timezone);
      props.onChange({ value: dt.valueOf(), operator: comparator });
      props.onClose();
    }
  };

  return (
    <Overlay onClose={props.onClose}>
      <div className='ipf-date-time-picker__date-time-picker-wrapper'>
        <div className='mb-3'>
          <div
            className='ipf-date-time-picker__date-time-close-button-bg'
            onClick={props.onClose}
          >
            <div className='ipf-date-time-picker__date-time-close-button-cross' />
          </div>
        </div>
        {!props.withoutComaparator && (
          <Dropdown
            baseClassName='datetimefilter-dropdown'
            name='comparator'
            onChange={(value: string) => setComparator(value)}
            options={dateTimeComparators}
            value={comparator}
          />
        )}
        <div className='my-4'>
          <input
            type='text'
            className='ipf-date-time-picker__date-time-input'
            onChange={withTargetValue(setInputValue)}
            value={inputValue}
          />
          {!isDateValid && <FormInputError error='Invalid date' />}
        </div>
        <div className='ipf-date-time-picker__date-time-wrapper'>
          <div className='mb-3'>
            <Calendar
              datetime={
                isDateValid
                  ? dateFromInputValue.getTime()
                  : new Date().getTime()
              }
              onChange={(value: number) =>
                setInputValue(format_1(value, props.outputDateFormat))
              }
            />
          </div>
        </div>
        <button
          type='button'
          className='ipf-date-time-picker__date-time-button'
          onClick={handleSaveButtonClick}
        >
          Save
        </button>
      </div>
    </Overlay>
  );
};
